import { useAppSelector } from "../../app/hooks";
import "../search/Search.css";
import { ResultBlockType } from "../../types";
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import { selectAddress } from "../address/addressSlice";
import { renderBuildingDataItem } from "../address/BuildingData";
import { BUILDING_MAP_INTERACTION } from "../../constants/buildingMetric";

import "./InfoResultBlock.css";
import { useTranslation } from "react-i18next";

// Helper component for metric items with info icon
function MetricItem({ data, title, details, alternate, onHistoricMonumentClick, onZoneSurveilleeClick, onFireStationClick, onGasStationClick, valueStyle }: { data: any, title?: string, details?: string, alternate?: string, onHistoricMonumentClick?: () => void, onZoneSurveilleeClick?: () => void, onFireStationClick?: () => void, onGasStationClick?: () => void, valueStyle?: React.CSSProperties }) {
  if (!data) return null;
  
  const label = data.label || '';
  const shortLabel = data.shortLabel || '';
  const value = renderBuildingDataItem(data);
  
  const hm = data.data;
  const hasHistoricAroundData =
    hm &&
    (Array.isArray(hm) ? hm.length > 0 : hm.features && hm.features.length > 0);
  const isHistoricMonuments =
    data.mapInteraction === BUILDING_MAP_INTERACTION.historicMonumentsAround &&
    hasHistoricAroundData;
  const isZoneSurveillee =
    data.mapInteraction === BUILDING_MAP_INTERACTION.zonesSurveillees &&
    !!data.data &&
    !!onZoneSurveilleeClick;
  const isFireStation =
    data.mapInteraction === BUILDING_MAP_INTERACTION.fireStation &&
    !!data.data?.features?.length &&
    !!onFireStationClick;
  const isGasStation =
    data.mapInteraction === BUILDING_MAP_INTERACTION.gasStation &&
    !!data.data?.features?.length &&
    !!onGasStationClick;
  const isMapClickable = isHistoricMonuments || isZoneSurveillee || isFireStation || isGasStation;

  const handleClick = () => {
    if (isHistoricMonuments && onHistoricMonumentClick) {
      onHistoricMonumentClick();
    } else if (isZoneSurveillee && onZoneSurveilleeClick) {
      onZoneSurveilleeClick();
    } else if (isFireStation && onFireStationClick) {
      onFireStationClick();
    } else if (isGasStation && onGasStationClick) {
      onGasStationClick();
    }
  };
  
  return (
    <div 
      className={`metric-item ${isMapClickable ? 'clickable' : ''}`}
      onClick={handleClick}
      style={{ 
        cursor: isMapClickable ? 'pointer' : 'default',
        transition: isMapClickable ? 'all 0.2s ease' : 'none'
      }}
      onMouseEnter={(e) => {
        if (isMapClickable) {
          e.currentTarget.style.backgroundColor = '#e3f2fd';
          e.currentTarget.style.transform = 'scale(1.02)';
        }
      }}
      onMouseLeave={(e) => {
        if (isMapClickable) {
          e.currentTarget.style.backgroundColor = '#f8f9fa';
          e.currentTarget.style.transform = 'scale(1)';
        }
      }}
    >
      <div className="metric-label">
        <span className="metric-label-text">{shortLabel}</span>
        <Tooltip 
          title={
            <>
              <h6 style={{ fontSize: "1.2em" }}>{label}</h6>
              {isMapClickable && (
                <p style={{ fontSize: "0.7rem", padding: "4px 0", color: "#ADD8E6" }}>
                  Cliquez pour voir sur la carte
                </p>
              )}
            </>
          }
          arrow
          placement="top"
        >
          <InfoIcon className="metric-label-icon" style={{ fontSize: "1rem" }} />
        </Tooltip>
      </div>
      <div className="metric-value" title={title} style={valueStyle}>
        <span>{value}</span>
        {details && <span>&nbsp;:&nbsp;{details}</span>}
        {alternate && <span>.&nbsp;{alternate}</span>}
      </div>
    </div>
  );
}

export default function InfoResultBlock({
  iconType,
  title,
  id,
  onHistoricMonumentClick,
  onZoneSurveilleeClick,
  onFireStationClick,
  onGasStationClick,
}: ResultBlockType & { onHistoricMonumentClick?: () => void, onZoneSurveilleeClick?: () => void, onFireStationClick?: () => void, onGasStationClick?: () => void }) {
  const address = useAppSelector(selectAddress);
  const { t } = useTranslation();
  
  // Debug log
  // console.log('InfoResultBlock props:', {
  //   onHistoricMonumentClick: !!onHistoricMonumentClick,
  //   onZoneSurveilleeClick: !!onZoneSurveilleeClick,
  //   id,
  //   title
  // });
  // const [elevation, setElevation] = useState<any>(null);

  // return (
  //   // <ul className="InfoResultBlock" style={{fontSize: "0.8em"}}>
  //   //   {renderBuildingData(address, "buildingData2")}
  //   //   {renderBuildingData(address, "buildingData1")}
  //   //   {/* <BuildingData buildingDataList="buildingData2" />
  //   //   <BuildingData style={{ flex: 1 }} buildingDataList="buildingData1" /> */}
  //   // </ul>
  // );

  return (
    <div className="InfoResultBlock">

      <div className="info-sections">

        {/* Building Data Section */}
        <div className="info-section">
          <div className="info-section-title">Batîment</div>
          <div className="info-section-content">
            Informations relatives au batîment.
          </div>
          <div className="metrics-grid metrics-grid--5">
            <MetricItem data={address.value?.buildingData1[0]} />
            <MetricItem data={address.value?.buildingData1[1]} />
            <MetricItem data={address.value?.buildingData1[2]} />
            <MetricItem data={address.value?.buildingData1[3]} />
            <MetricItem data={address.value?.buildingData1[4]} />
            <MetricItem data={address.value?.buildingData1[5]} valueStyle={{ textTransform: 'capitalize' }} />
            <MetricItem data={address.value?.buildingData1[6]} valueStyle={{ textTransform: 'capitalize' }} />
            <MetricItem data={address.value?.buildingData1[7]} onZoneSurveilleeClick={onZoneSurveilleeClick} />
            <MetricItem data={address.value?.buildingData1[8]} onZoneSurveilleeClick={onZoneSurveilleeClick} />
            <MetricItem data={address.value?.buildingData1[9]} onZoneSurveilleeClick={onZoneSurveilleeClick} />
            {/* 
            <MetricItem data={address.value?.buildingData1[10]} onZoneSurveilleeClick={onZoneSurveilleeClick} />
            <MetricItem data={address.value?.buildingData1[11]} onZoneSurveilleeClick={onZoneSurveilleeClick} /> 
            */}
            <MetricItem data={address.value?.buildingData1[12]} onZoneSurveilleeClick={onZoneSurveilleeClick} />
            <MetricItem data={address.value?.buildingData1[13]} />
            <MetricItem data={address.value?.buildingData1[14]} />
            <MetricItem data={address.value?.buildingData1[15]} onHistoricMonumentClick={onHistoricMonumentClick} />
            <MetricItem data={address.value?.buildingData1[16]} />
          </div>
        </div>

        {/* Information Sections */}
        <div className="info-section">
          <div className="info-section-title">Terrain</div>
          <div className="info-section-content">
            {t("fireStation.terrainDescription")}
          </div>
          <div className="metrics-grid metrics-grid--5">
            <MetricItem data={address.value?.buildingData2[0]} />
            <MetricItem data={address.value?.buildingData2[5]} title={t(`${address.value?.buildingData2[5].value}`)} />
            <MetricItem data={address.value?.buildingData2[1]} />
            <MetricItem data={address.value?.buildingData2[2]} />
            <MetricItem data={address.value?.buildingData2[3]} />
            <MetricItem data={address.value?.buildingData2[4]} />
            <MetricItem data={address.value?.buildingData2[6]} />
            <MetricItem data={address.value?.buildingData2[7]} />
            <MetricItem data={address.value?.buildingData2[8]} onFireStationClick={onFireStationClick} />
            <MetricItem data={address.value?.buildingData2[9]} onGasStationClick={onGasStationClick} />
          </div>
        </div>

      </div>

    </div>
  );
}
